import { ProfileEditor } from "@/components/settings/ProfileEditor";
import { AccountDeletionRequest } from "@/components/settings/AccountDeletionRequest";
import { NotificationPreferences } from "@/components/settings/NotificationPreferences";
import { AccountDataLink } from "@/components/AccountDataLink";
import { apiErrorText } from "@/lib/apiError";
import { AnalyticsPreferences } from "@/components/AnalyticsPreferences";
import { useTranslation } from "react-i18next";
import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LogOut } from "lucide-react";
import { changePassword } from "@/api/auth";
import { getProfile } from "@/api/profile";
import { listJobs } from "@/api/coach";
import { getMySubscription, createPortalSession, type Subscription } from "@/api/subscription";
import { useAuthStore } from "@/stores/auth";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { LoadError } from "@/components/ui/LoadError";
import { IntervalsCard } from "@/components/settings/IntervalsCard";
import { EventsSection } from "@/components/settings/EventsSection";
import { SyncStatusCard } from "@/components/settings/SyncStatusCard";
import { LanguageSection } from "@/components/settings/LanguageSection";
import { TimezoneSection } from "@/components/settings/TimezoneSection";
import { DailyCheckTimeSection } from "@/components/settings/DailyCheckTimeSection";
import { fmtDate } from "@/lib/dates";

export default function Settings() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const email = useAuthStore((s) => s.email);

  const profileQ = useQuery({ queryKey: ["profile"], queryFn: getProfile });
  const jobsQ = useQuery({ queryKey: ["jobs"], queryFn: listJobs });

  const [showJobs, setShowJobs] = useState(false);

  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  async function handleChangePassword(e: FormEvent) {
    e.preventDefault();
    setPwError(null);
    setPwSuccess(false);
    if (pwNew.length < 8) {
      setPwError(t("settings.password_min8"));
      return;
    }
    if (pwCurrent === pwNew) {
      setPwError(t("settings.password_must_differ"));
      return;
    }
    setPwBusy(true);
    try {
      await changePassword(pwCurrent, pwNew);
      logout();
      nav("/login?pw=changed", { replace: true });
      setPwSuccess(true);
      setPwCurrent("");
      setPwNew("");
    } catch (err) {
      setPwError(apiErrorText(err, t("settings.password_change_failed")));
    } finally {
      setPwBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <SectionLabel>{t("settings.title")}</SectionLabel>
        <h1 className="mt-1 font-serif text-[26px] font-medium">{t("settings.profile")}</h1>
      </div>

      <Card>
        <SectionLabel>{t("settings.account")}</SectionLabel>
        <div className="mt-2 text-[14px] text-navy-deep">{email}</div>
        {profileQ.data ? (
          <dl className="mt-3 grid grid-cols-2 gap-2 text-[13px]">
            <Row label={t("settings.age")} value={profileQ.data.age} />
            <Row label={t("settings.weight_kg")} value={profileQ.data.weight_kg} />
            <Row label={t("settings.height_cm")} value={profileQ.data.height_cm} />
            <Row label={t("settings.sex")} value={profileQ.data.sex} />
            <Row label={t("settings.weekly_hours")} value={profileQ.data.weekly_hours_available} />
            <Row label={t("profile_editor.long_session_day")} value={profileQ.data.long_session_day ? t(`profile_editor.${profileQ.data.long_session_day}`, profileQ.data.long_session_day) : null} />
            <Row label={t("race_dates.main_event")} value={profileQ.data.main_race?.name ?? profileQ.data.target_race_type} />
            <Row label={t("race_dates.main_date")} value={profileQ.data.target_race_date ? fmtDate(profileQ.data.target_race_date, "d MMMM yyyy") : null} />
            <Row label={t("race_dates.next_event")} value={profileQ.data.next_race?.name} />
            <Row label={t("race_dates.next_date")} value={profileQ.data.next_race?.date ? fmtDate(profileQ.data.next_race.date, "d MMMM yyyy") : null} />
          </dl>
        ) : null}
      </Card>

      <ProfileEditor />
      <AnalyticsPreferences />
      <NotificationPreferences />
      <Card><AccountDataLink /></Card>
      <AccountDeletionRequest />

      <Card>
        <SectionLabel>{t("settings.change_password")}</SectionLabel>
        <form className="mt-3 space-y-3" onSubmit={handleChangePassword}>
          <Input
            type="password"
            label={t("settings.current_password")}
            value={pwCurrent}
            onChange={(e) => setPwCurrent(e.target.value)}
            autoComplete="current-password"
            required
          />
          <Input
            type="password"
            label={t("settings.new_password")}
            value={pwNew}
            onChange={(e) => setPwNew(e.target.value)}
            minLength={8}
            autoComplete="new-password"
            required
          />
          {pwError ? (
            <div className="rounded-lg bg-coral/10 px-3 py-2 text-[13px] text-coral">
              {pwError}
            </div>
          ) : null}
          {pwSuccess ? (
            <div className="rounded-lg bg-amber/10 px-3 py-2 text-[13px] text-amber-dark">
              {t("settings.password_changed")}
            </div>
          ) : null}
          <Button type="submit" size="md" loading={pwBusy}>
            {t("settings.change_password")}
          </Button>
        </form>
      </Card>

      <SubscriptionCard />

      <LanguageSection />

      <IntervalsCard />

      <EventsSection />

      <SyncStatusCard />

      <Card>
        <SectionLabel>{t("settings.zones")}</SectionLabel>
        {profileQ.data ? (
          <dl className="mt-2 grid grid-cols-2 gap-2 text-[13px]">
            <Row label={t("settings.bike_ftp")} value={profileQ.data.zones.bike_ftp_watts} />
            <Row label={t("settings.swim_css")} value={profileQ.data.zones.swim_css_per_100m_sec} />
            <Row label={t("settings.run_threshold")} value={profileQ.data.zones.run_threshold_pace_sec_per_km} />
            <Row label={t("settings.run_lthr")} value={profileQ.data.zones.run_lthr_bpm} />
          </dl>
        ) : null}
      </Card>

      <Card>
        <div className="flex items-center justify-between">
          <SectionLabel>{t("settings.scheduler")}</SectionLabel>
          <button
            onClick={() => setShowJobs((v) => !v)}
            className="text-[12px] text-amber-dark hover:underline"
          >
            {showJobs ? t("settings.hide") : t("settings.show_jobs")}
          </button>
        </div>
        {showJobs && jobsQ.data ? (
          <ul className="mt-3 space-y-2 text-[12px]">
            {jobsQ.data.jobs.map((j) => (
              <li key={j.id} className="rounded-lg bg-cream px-3 py-2">
                <div className="font-medium text-navy-deep">{j.id}</div>
                <div className="text-taupe">{j.name}</div>
                <div className="text-taupe">{t("settings.next_run", { time: j.next_run_time ?? "—" })}</div>
              </li>
            ))}
          </ul>
        ) : null}
      </Card>

      <TimezoneSection />

      <DailyCheckTimeSection />

      <Button
        variant="danger"
        className="w-full"
        onClick={() => {
          logout();
          nav("/login", { replace: true });
        }}
      >
        <LogOut className="h-4 w-4" /> {t("settings.logout")}
      </Button>
    </div>
  );
}

function SubscriptionCard() {
  const { t } = useTranslation();
  const [portalLoading, setPortalLoading] = useState(false);
  const subQ = useQuery({
    queryKey: ["my-subscription"],
    queryFn: getMySubscription,
  });

  async function handleManage() {
    setPortalLoading(true);
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (err) {
      alert(apiErrorText(err, t("billing.portal.error")));
      setPortalLoading(false);
    }
  }

  const sub: Subscription | null = subQ.data?.subscription ?? null;

  if (subQ.isLoading) {
    return (
      <Card>
        <SectionLabel>{t("billing.subscription.title")}</SectionLabel>
        <div className="mt-2 text-[13px] text-taupe">{t("billing.loading")}</div>
      </Card>
    );
  }

  if (subQ.isError) {
    return <Card>
      <SectionLabel>{t("billing.subscription.title")}</SectionLabel>
      <LoadError onRetry={() => void subQ.refetch()} retrying={subQ.isFetching} />
    </Card>;
  }

  if (!sub) {
    return (
      <Card>
        <SectionLabel>{t("billing.subscription.title")}</SectionLabel>
        <div className="mt-3 text-[13px] text-taupe">
          {t("billing.subscription.none_active")}
        </div>
        <a
          href="/pricing"
          className="mt-3 inline-block text-[13px] text-amber-dark underline"
        >
          {t("billing.subscription.view_pricing")}
        </a>
      </Card>
    );
  }

  return (
    <Card>
      <SectionLabel>{t("billing.subscription.title")}</SectionLabel>
      <div className="mt-3 space-y-2 text-[13px]">
        <div className="flex items-center gap-2">
          <span className="text-taupe">{t("billing.subscription.tier_label")}:</span>
          {sub.tier === "founding" ? (
            <span className="rounded bg-amber/15 px-2 py-0.5 text-[12px] font-medium text-amber-dark">
              {t("billing.tier.founding")}
            </span>
          ) : (
            <span className="font-medium text-navy-deep">
              {t(`billing.tier.${sub.tier}`, sub.tier)}
            </span>
          )}
        </div>
        <div>
          <span className="text-taupe">{t("billing.subscription.status")}:</span>{" "}
          <span className="font-medium text-navy-deep">
            {t(`billing.status.${sub.status}`, sub.status)}
          </span>
          {sub.cancel_at_period_end ? (
            <span className="ml-2 text-coral">
              ({t("billing.subscription.cancels_at_period_end")})
            </span>
          ) : null}
        </div>
        {sub.current_period_end ? (
          <div>
            <span className="text-taupe">{t("billing.subscription.next_billing")}:</span>{" "}
            <span className="metric-number">
              {new Date(sub.current_period_end).toLocaleDateString()}
            </span>
          </div>
        ) : null}
        {sub.grace_period_until ? (
          <div className="text-coral">
            {t("billing.subscription.grace_until", {
              date: new Date(sub.grace_period_until).toLocaleDateString(),
            })}
          </div>
        ) : null}
      </div>
      <Button className="mt-4" size="md" loading={portalLoading} onClick={handleManage}>
        {t("billing.portal.manage_button")}
      </Button>
    </Card>
  );
}


function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-cream px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-taupe">{label}</div>
      <div className="metric-number text-[14px]">{value ?? "—"}</div>
    </div>
  );
}
